import type { NotificationPreferences, OptionalEmailCategory } from "@kontaktfix/shared";
import { Card, Skeleton, Switch } from "@kontaktfix/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Page, PageHeader } from "@/components/page";
import { api } from "@/lib/api";
import { toastError } from "@/lib/errors";

const CATEGORIES: OptionalEmailCategory[] = ["product", "lifecycle", "updates"];
const KEY = ["notification-preferences"] as const;

/** Account > Notifications: which optional emails this person receives. */
export function NotificationsPage() {
  const { t } = useTranslation();
  const client = useQueryClient();
  const { data } = useQuery({
    queryKey: KEY,
    queryFn: () => api.get<NotificationPreferences>("/api/account/notifications"),
  });
  const save = useMutation({
    mutationFn: (next: NotificationPreferences) =>
      api.put<NotificationPreferences>("/api/account/notifications", {
        product: next.product,
        lifecycle: next.lifecycle,
        updates: next.updates,
      }),
    onSuccess: (prefs) => client.setQueryData(KEY, prefs),
    onError: toastError,
  });

  return (
    <Page>
      <PageHeader
        title={t("notifications.title")}
        description={t("notifications.description", { email: data?.email ?? "" })}
      />
      <Card>
        {!data ? (
          <Skeleton className="m-4 h-24" />
        ) : (
          <div className="divide-y divide-border">
            {CATEGORIES.map((c) => (
              <label key={c} className="flex items-start justify-between gap-6 px-5 py-4">
                <span>
                  <span className="block text-[13.5px] font-medium">{t(`notifications.${c}.title`)}</span>
                  <span className="mt-0.5 block text-[13px] text-fg-muted">
                    {t(`notifications.${c}.hint`)}
                  </span>
                </span>
                <Switch
                  checked={data[c]}
                  disabled={save.isPending}
                  aria-label={t(`notifications.${c}.title`)}
                  onCheckedChange={(on) => save.mutate({ ...data, [c]: on })}
                />
              </label>
            ))}
            <p className="px-5 py-4 text-[12.5px] text-fg-subtle">{t("notifications.transactional")}</p>
          </div>
        )}
      </Card>
    </Page>
  );
}
